import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { Datumsfeld } from '../ui/Datumsfeld.js'
import { Zeichen } from '../ui/Zeichen.js'
import { rappenAlsText, zuRappen } from '../geld.js'
import type { Sprung } from '../net/apiClient.js'
import { notify, notifyError } from '../ui/Toasts.js'
import {
  api,
  type ZahlungsFilter,
  type ZahlungsListe,
  type ZahlungsZeile,
  type ZahlungsHistorie,
  type HistorieZeile,
  type Zeitraum
} from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Zahlungen – ein eigener Bereich, nicht ein Kästchen am Beleg.
 *
 * Die Frage, die hier beantwortet wird, ist nicht "wie steht diese eine
 * Rechnung", sondern "wer schuldet uns was". Dafür musste man bisher wissen,
 * welche Rechnung man sucht, bevor man nachsehen konnte – also genau das, was
 * man herausfinden wollte.
 *
 * Der Zahlungsstatus steht nirgends in der Datenbank. Er ergibt sich aus der
 * Summe der gültigen Zahlungen gegen das Total, gerechnet mit derselben
 * Funktion wie im Dashboard und auf dem Beleg. Ein gespeicherter Merker müsste
 * bei jeder Zahlung, jeder Stornierung und jeder Betragsänderung nachgezogen
 * werden – und beim Geld ist "irgendwann falsch" die teuerste Art Fehler.
 */

/* Beide stehen in ../geld.ts – einmal, für die Zahlungsbildschirme. */
const rp = rappenAlsText

const datum = (iso: string | null): string =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-CH') : '–'

const STATUS: Record<string, { text: string; klasse: string }> = {
  offen: { text: 'offen', klasse: 'o-badge-neutral' },
  teilbezahlt: { text: 'teilbezahlt', klasse: 'o-badge-warn' },
  bezahlt: { text: 'bezahlt', klasse: 'o-badge-ok' },
  ueberbezahlt: { text: 'überbezahlt', klasse: 'o-badge-alert' },
  storniert: { text: 'storniert', klasse: 'o-badge-neutral' }
}

/**
 * Die drei Zeiträume – und warum es nur drei sind.
 *
 * Gefragt wird im Büro nach dem Monat (für den Abschluss) und nach dem Jahr
 * (für den Treuhänder). «Alles» ist kein Zeitraum, sondern der Verzicht
 * darauf, und steht dabei, weil man sonst nicht nachrechnen kann, ob die
 * Monate zusammen das Ganze ergeben.
 *
 * Ein freies Von-Bis gibt es nicht. Es wäre ein viertes Eingabefeld für eine
 * Frage, die in diesem Bildschirm selten gestellt wird – und wer sie stellt,
 * findet sie in der Rechnungsliste mit ihrem Datumsbereich.
 */
const ZEITRAEUME: Array<{ id: Zeitraum; label: string; kurz: string }> = [
  { id: 'monat', label: 'Dieser Monat', kurz: 'diesen Monat' },
  { id: 'jahr', label: 'Dieses Jahr', kurz: 'dieses Jahr' },
  { id: 'alles', label: 'Alles', kurz: 'insgesamt' }
]

const FILTER: Array<{ id: ZahlungsFilter; label: string }> = [
  { id: 'offen', label: 'Offen' },
  { id: 'ueberfaellig', label: 'Überfällig' },
  { id: 'teilbezahlt', label: 'Teilbezahlt' },
  { id: 'bezahlt', label: 'Bezahlt' },
  { id: 'ueberbezahlt', label: 'Überbezahlt' },
  /*
    Storniertes bekommt einen eigenen Reiter und läuft unter «Alle» nicht mit –
    dieselbe Regel wie in der Rechnungsübersicht. Eine stornierte Rechnung
    fordert kein Geld mehr; zwischen den Forderungen stehend war sie nur im
    Weg, und wegbekommen hat sie niemand.
  */
  { id: 'storniert', label: 'Storniert' },
  { id: 'alle', label: 'Alle' }
]

export function Payments({
  session,
  aufBeleg,
  aufListe
}: {
  session: SessionUser
  aufBeleg?: (id: string) => void
  /**
   * Der Weg zur Sammelerfassung.
   *
   * Mehrere Zahlungen auf einmal werden in der Rechnungsliste erfasst: Dort
   * gibt es die Auswahl, und dort stehen die offenen Beträge. Ein zweiter
   * Satz Kästchen hier wäre eine zweite Umsetzung derselben Sache – und wer
   * auf diesem Bildschirm steht, soll trotzdem wissen, dass es den Weg gibt.
   */
  aufListe?: (ziel: Sprung) => void
}): JSX.Element {
  const [ansicht, setAnsicht] = useState<'rechnungen' | 'historie'>('rechnungen')
  const [filter, setFilter] = useState<ZahlungsFilter>('offen')
  const [suche, setSuche] = useState('')
  const [liste, setListe] = useState<ZahlungsListe | null>(null)
  const [historie, setHistorie] = useState<ZahlungsHistorie | null>(null)
  const [historieZeige, setHistorieZeige] = useState<'gueltig' | 'storniert' | 'alle'>('alle')
  /*
    EIN Zeitraum für die Kachel und für die Liste dahinter.

    Zwei getrennte Auswahlen wären der kurze Weg zur Frage «warum zeigt die
    Kachel 12'400 und die Liste darunter 3'100» – und auf die gibt es keine
    gute Antwort.
  */
  const [zeitraum, setZeitraum] = useState<Zeitraum>('monat')
  const [laedt, setLaedt] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  /** Die Rechnung, für die gerade erfasst wird. */
  const [erfassen, setErfassen] = useState<ZahlungsZeile | null>(null)
  const [betrag, setBetrag] = useState('')
  const [zahldatum, setZahldatum] = useState(new Date().toISOString().slice(0, 10))
  const [notiz, setNotiz] = useState('')

  /** Die Zahlung, die gerade storniert wird. */
  const [storno, setStorno] = useState<string | null>(null)
  const [grund, setGrund] = useState('')

  /** Die Zahlung, die gerade berichtigt wird – und ihre neuen Werte. */
  const [korrektur, setKorrektur] = useState<HistorieZeile | null>(null)
  const [kBetrag, setKBetrag] = useState('')
  const [kDatum, setKDatum] = useState('')
  const [kNotiz, setKNotiz] = useState('')
  const [kGrund, setKGrund] = useState('')
  const [kUeberzahlung, setKUeberzahlung] = useState(false)

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      const [l, h] = await Promise.all([
        api.paymentList(filter, suche, zeitraum),
        api.paymentHistory(historieZeige, suche, zeitraum)
      ])
      setListe(l)
      setHistorie(h)
    } catch (err) {
      notifyError(err)
    } finally {
      setLaedt(false)
    }
  }, [filter, suche, historieZeige, zeitraum])

  useEffect(() => {
    void laden()
  }, [laden])

  function erfassenOeffnen(z: ZahlungsZeile): void {
    /*
      Zweiter Riegel an derselben Regel: Auch wenn diese Funktion je von
      woanders aufgerufen wird, öffnet sie für eine stornierte Rechnung kein
      Formular. Der Knopf dazu steht nicht in der Liste (siehe unten), und der
      Server lehnt es ohnehin ab – drei Stellen, eine Regel.
    */
    if (z.belegstatus === 'storniert') {
      notify.info('Diese Rechnung ist storniert. Darauf wird keine Zahlung mehr erfasst.')
      return
    }
    setErfassen(z)
    // Der Vorschlag ist der offene Betrag: in den allermeisten Fällen zahlt
    // der Kunde genau das, und Abtippen ist die häufigste Fehlerquelle.
    setBetrag((z.offenRp / 100).toFixed(2))
    setZahldatum(new Date().toISOString().slice(0, 10))
    setNotiz('')
  }

  async function speichern(): Promise<void> {
    if (!erfassen) return
    const rappen = zuRappen(betrag)
    if (rappen <= 0) throw new Error('Der Betrag muss grösser als null sein.')
    setBusy('zahlung')
    try {
      await api.recordPayment(
        erfassen.id,
        { amountRp: rappen, paidAt: zahldatum, note: notiz.trim() || undefined },
        // Eine Kennung je Erfassung: ein zweiter Klick schickt dieselbe und
        // erzeugt deshalb keine zweite Zahlung.
        uuidv7()
      )
      notify.ok(`CHF ${rp(rappen)} auf ${erfassen.number} erfasst.`)
      setErfassen(null)
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  function korrekturOeffnen(z: HistorieZeile): void {
    setKorrektur(z)
    setKBetrag((z.amountRp / 100).toFixed(2))
    setKDatum(z.paidAt.slice(0, 10))
    setKNotiz(z.note ?? '')
    setKGrund('')
    setKUeberzahlung(false)
  }

  /**
   * Berichtigen – ein Aufruf, zwei Buchungen.
   *
   * Der Server storniert die alte Zeile und schreibt die neue in derselben
   * Transaktion. Hier steht deshalb kein zweistufiger Ablauf: Es gibt keinen
   * Zwischenzustand, in dem die Zahlung weg und die Berichtigung noch nicht da
   * wäre – und damit auch keinen Augenblick, in dem die Rechnung fälschlich
   * offen aussieht.
   */
  async function korrigieren(): Promise<void> {
    if (!korrektur || !kGrund.trim()) return
    const rappen = zuRappen(kBetrag)
    if (rappen <= 0) {
      notifyError(new Error('Der Betrag muss grösser als null sein.'))
      return
    }
    setBusy('korrektur')
    try {
      await api.correctPayment(
        korrektur.id,
        {
          amountRp: rappen,
          paidAt: kDatum,
          note: kNotiz.trim() || undefined,
          grund: kGrund.trim(),
          ueberzahlungOk: kUeberzahlung || undefined
        },
        uuidv7()
      )
      notify.ok(
        `Korrigiert: ${rp(rappen)} auf ${korrektur.number}. Die alte Zahlung bleibt storniert sichtbar.`
      )
      setKorrektur(null)
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  async function stornieren(): Promise<void> {
    if (!storno || !grund.trim()) return
    setBusy('storno')
    try {
      await api.voidPayment(storno, grund.trim(), uuidv7())
      notify.ok('Zahlung storniert. Sie bleibt sichtbar und zählt nicht mehr mit.')
      setStorno(null)
      setGrund('')
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  const gesperrt = !session.sensitiveAllowed
  const lage = liste?.lage ?? null
  const eingang = liste?.eingang ?? null
  const zr = ZEITRAEUME.find((z) => z.id === zeitraum) ?? ZEITRAEUME[0]

  /** «01.10.2026 – 31.10.2026», oder «insgesamt», wenn kein Zeitraum gilt. */
  const zeitraumText =
    eingang && eingang.vonTag && eingang.bisTag
      ? `${datum(eingang.vonTag)} – ${datum(eingang.bisTag)}`
      : 'ohne Einschränkung'

  /** Auf eine Kennzahl drücken heisst: ihre Liste öffnen. */
  function aufFilter(f: ZahlungsFilter): void {
    setAnsicht('rechnungen')
    setFilter(f)
  }

  return (
    <>
      <OfficeHead
        titel="Zahlungen"
        /*
          Hier stand «offen in dieser Auswahl». Der Satz war richtig und als
          Auskunft unbrauchbar: Die Zahl änderte sich mit jedem Reiter, und wer
          wissen will, wie viel Geld draussen ist, will eine Zahl und nicht
          sieben. Jetzt steht die Lage des Betriebs da – über alle Rechnungen.
        */
        unter={
          lage
            ? `CHF ${rp(lage.offenRp)} offen · CHF ${rp(lage.ueberfaelligRp)} davon überfällig`
            : 'Wird geladen …'
        }
      >
        {aufListe && (
          <button
            className="o-btn"
            id="btn-mehrere-zahlungen"
            title="In der Rechnungsliste mehrere Rechnungen anhaken und in einem Vorgang verbuchen"
            onClick={() => aufListe({ bereich: 'rechnungen', filter: 'offen' })}
          >
            Mehrere erfassen
          </button>
        )}
        <button
          className={ansicht === 'rechnungen' ? 'o-btn o-btn-primary' : 'o-btn'}
          id="btn-ansicht-rechnungen"
          onClick={() => setAnsicht('rechnungen')}
        >
          Rechnungen
        </button>
        <button
          className={ansicht === 'historie' ? 'o-btn o-btn-primary' : 'o-btn'}
          id="btn-ansicht-historie"
          onClick={() => setAnsicht('historie')}
        >
          Zahlungshistorie
        </button>
        {/*
          camt bleibt sichtbar und tot geschaltet, mit Grund am Knopf. Ein
          Knopf, der nichts tut, wäre schlimmer als keiner – und gar kein
          Hinweis liesse das Büro rätseln, ob Raporto Bankdateien je kann.
        */}
        <button className="o-btn" id="btn-camt" disabled title="Folgt in einem späteren Schritt">
          camt-Import folgt später
        </button>
      </OfficeHead>

      {gesperrt && (
        <div className="o-band" id="band-zahlungen-gesperrt">
          <span>
            <strong>Erfassen und Stornieren sind gesperrt.</strong> Solange kein zweiter Faktor
            eingerichtet ist, kannst du die Zahlungen ansehen, aber nicht ändern.
          </span>
        </div>
      )}

      {/* ---------- Die Finanzlage in vier Zahlen ---------- */}

      {/*
        Vier Kacheln, und die vierte ist die wichtigste Unterscheidung dieses
        Bildschirms: Die ersten drei zählen FORDERUNGEN – was fakturiert ist
        und noch fehlt. Die vierte zählt GELD, das hereingekommen ist.

        Beides in eine Kachel «Umsatz» zu legen, wäre die bequeme Lüge: Eine
        Rechnung über 10'000 im Januar, bezahlt im März, gehört im Januar ins
        Rechnungsvolumen und im März in den Zahlungseingang. Eine gemeinsame
        Zahl stimmte in keinem der beiden Monate.

        Deshalb hat nur die vierte Kachel einen Zeitraum, und er steht daran.
        Eine Forderung hat keinen: Eine Rechnung, die seit vier Monaten offen
        ist, gehört auf jeden Blick.

        Alle vier Zahlen kommen aus einer SQL-Abfrage über ALLE Rechnungen
        (`services/belegsql.ts`) und nicht aus der Liste darunter – die ist
        gekürzt, die Zahlen sind es nicht. Und jede führt in genau die Liste,
        die sie ergibt: derselbe Filter, dieselbe Bedingung, dieselbe Zahl.
      */}
      <div className="o-kennzahlen" id="zahlungs-kennzahlen">
        <button
          type="button"
          className="o-kennzahl"
          id="kz-zahl-offen"
          disabled={(liste?.zaehler.offen ?? 0) === 0}
          onClick={() => aufFilter('offen')}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="rechnung" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Offen</span>
          <span className="o-kennzahl-wert" id="kz-zahl-offen-wert">
            CHF {rp(lage?.offenRp ?? 0)}
          </span>
          <span className="o-kennzahl-unter ok">
            {liste?.zaehler.offen ?? 0} Rechnungen
          </span>
        </button>

        <button
          type="button"
          id="kz-zahl-ueberfaellig"
          className={
            (lage?.ueberfaelligAnzahl ?? 0) > 0 ? 'o-kennzahl alarm' : 'o-kennzahl'
          }
          disabled={(lage?.ueberfaelligAnzahl ?? 0) === 0}
          onClick={() => aufFilter('ueberfaellig')}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="zahlung" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Überfällig</span>
          <span className="o-kennzahl-wert" id="kz-zahl-ueberfaellig-wert">
            CHF {rp(lage?.ueberfaelligRp ?? 0)}
          </span>
          <span
            className={
              (lage?.ueberfaelligAnzahl ?? 0) > 0
                ? 'o-kennzahl-unter alarm'
                : 'o-kennzahl-unter ok'
            }
          >
            {lage?.ueberfaelligAnzahl ?? 0} Rechnungen · Frist vorbei
          </span>
        </button>

        <button
          type="button"
          className="o-kennzahl"
          id="kz-zahl-teilbezahlt"
          disabled={(lage?.teilbezahltAnzahl ?? 0) === 0}
          onClick={() => aufFilter('teilbezahlt')}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="haken" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Teilbezahlt</span>
          <span className="o-kennzahl-wert" id="kz-zahl-teilbezahlt-wert">
            CHF {rp(lage?.teilbezahltRp ?? 0)}
          </span>
          <span className="o-kennzahl-unter ok">
            {lage?.teilbezahltAnzahl ?? 0} Rechnungen · Restbetrag
          </span>
        </button>

        {/*
          Die Eingangskachel führt in die Historie und nicht in eine
          Rechnungsliste: Gefragt ist hier, welche ZAHLUNGEN die Zahl ergeben –
          mit Datum, Betrag und Erfasser. Der Zeitraum bleibt derselbe.
        */}
        <button
          type="button"
          className="o-kennzahl"
          id="kz-zahl-eingang"
          disabled={(eingang?.anzahl ?? 0) === 0}
          onClick={() => {
            setAnsicht('historie')
            setHistorieZeige('gueltig')
          }}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="kalender" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Eingegangen · {zr.label}</span>
          <span className="o-kennzahl-wert" id="kz-zahl-eingang-wert">
            CHF {rp(eingang?.betragRp ?? 0)}
          </span>
          <span className="o-kennzahl-unter ok" id="kz-zahl-eingang-zeitraum">
            {eingang?.anzahl ?? 0} Zahlungen · {zeitraumText}
          </span>
        </button>
      </div>

      {/* ---------- Zeitraum ---------- */}

      {/*
        Die Auswahl steht UNTER den Kacheln und nicht darüber: Sie betrifft nur
        die vierte. Stünde sie oben, läse sie sich wie ein Filter über alles –
        und dann fragt jemand zu Recht, warum «Dieser Monat» die überfälligen
        Rechnungen aus dem letzten Quartal nicht wegnimmt.
      */}
      <div className="o-card">
        <div className="o-pad">
          {/*
            Die Beschriftung steht VOR den Knöpfen und nicht als Satz darunter.
            Ein Zeitraumwähler ohne Bezug liest sich wie ein Filter über alles –
            und dann fragt zu Recht jemand, warum «Dieser Monat» die überfällige
            Rechnung aus dem letzten Quartal nicht wegnimmt.
          */}
          <div className="o-filter" id="zeitraumfilter">
            <span className="o-soft" style={{ alignSelf: 'center', marginRight: 4 }}>
              Zahlungseingang:
            </span>
            {ZEITRAEUME.map((z) => (
              <button
                key={z.id}
                id={`zeitraum-${z.id}`}
                className={z.id === zeitraum ? 'o-chip aktiv' : 'o-chip'}
                onClick={() => setZeitraum(z.id)}
              >
                {z.label}
              </button>
            ))}
          </div>
          <p className="o-soft" style={{ margin: '12px 0 0' }}>
            Offene und überfällige Rechnungen stehen unabhängig davon – eine Forderung verfällt
            nicht mit dem Monatsende.
          </p>
        </div>
      </div>

      {/* ---------- Überzahlungen ---------- */}

      {/*
        Eine Überzahlung wird gezeigt und nicht verrechnet.

        Automatisch auf die nächste Rechnung anzurechnen wäre bequem und
        falsch: Der Betrag gehört dem Kunden, und was mit ihm geschieht –
        zurückzahlen, anrechnen, stehen lassen – entscheidet der Betrieb und
        nicht ein Programm. Deshalb steht hier ein Hinweis mit dem Weg zur
        Liste und keine Verrechnung.

        «warnung» und nicht der Alarmton: Zu viel Geld ist kein Notfall,
        sondern eine Sache, die geklärt werden muss. Der rote Streifen ist für
        die überfällige Forderung reserviert – wer ihn für beides benutzt, hat
        ihn für keines.
      */}
      {(lage?.ueberbezahltAnzahl ?? 0) > 0 && (
        <div className="o-band warnung" id="band-ueberbezahlt">
          <span>
            <strong>
              {lage?.ueberbezahltAnzahl}{' '}
              {lage?.ueberbezahltAnzahl === 1 ? 'Rechnung ist' : 'Rechnungen sind'} überbezahlt
            </strong>{' '}
            – CHF {rp(lage?.ueberbezahltRp ?? 0)} zu viel. Raporto rechnet das nicht von selbst an
            und zahlt nichts zurück; beides entscheidest du.
          </span>
          <button className="o-btn" id="btn-zu-ueberbezahlt" onClick={() => aufFilter('ueberbezahlt')}>
            Anzeigen
          </button>
        </div>
      )}

      {ansicht === 'rechnungen' ? (
        <>
          {/* ---------- Filter und Suche ---------- */}

          <div className="o-card">
            <div className="o-pad">
              <div className="o-filter" id="zahlungsfilter">
                {FILTER.map((f) => (
                  <button
                    key={f.id}
                    id={`filter-${f.id}`}
                    className={f.id === filter ? 'o-chip aktiv' : 'o-chip'}
                    onClick={() => setFilter(f.id)}
                  >
                    {f.label}
                    <span className="o-chip-zahl">{liste?.zaehler[f.id] ?? 0}</span>
                  </button>
                ))}
              </div>
              <div className="o-field" style={{ marginTop: 16, maxWidth: 420 }}>
                <label htmlFor="zahl-suche">Suche</label>
                <input
                  id="zahl-suche"
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                  placeholder="Rechnungsnummer, Kunde oder Projekt"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* ---------- Erfassungsformular ---------- */}

          {erfassen && (
            <div className="o-card" id="zahlung-erfassen">
              <div className="o-card-head">
                <h3>
                  Zahlung auf <span className="o-mono">{erfassen.number}</span> ·{' '}
                  {erfassen.customerName}
                </h3>
                <span className="o-soft">Offen: CHF {rp(erfassen.offenRp)}</span>
              </div>
              <div className="o-pad">
                <div className="o-grid o-grid-3">
                  <div className="o-field">
                    <label htmlFor="z-betrag">Betrag CHF</label>
                    <input
                      autoComplete="off"
                      id="z-betrag"
                      inputMode="decimal"
                      value={betrag}
                      onChange={(e) => setBetrag(e.target.value)}
                    />
                    <span className="hint">Teilzahlungen sind erlaubt.</span>
                  </div>
                  <div className="o-field">
                    <label htmlFor="z-datum">Zahlungsdatum</label>
                    <Datumsfeld
                      id="z-datum"
                      value={zahldatum}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={setZahldatum}
                    />
                  </div>
                  <div className="o-field">
                    <label htmlFor="z-notiz">Notiz</label>
                    <input
                      autoComplete="off"
                      id="z-notiz"
                      value={notiz}
                      onChange={(e) => setNotiz(e.target.value)}
                      placeholder="optional, z.B. Bankauszug 12.09."
                    />
                  </div>
                </div>
                {zuRappen(betrag) > erfassen.offenRp && (
                  <p className="o-soft" style={{ marginTop: 10 }}>
                    Das ist mehr als offen ist. Die Rechnung wird danach als überbezahlt geführt –
                    erlaubt, aber sichtbar.
                  </p>
                )}
                <div className="office-actions" style={{ marginTop: 14 }}>
                  <button
                    className="o-btn o-btn-primary"
                    id="btn-zahlung-speichern-bereich"
                    disabled={busy !== null || zuRappen(betrag) <= 0}
                    onClick={() => void speichern()}
                  >
                    {busy === 'zahlung' ? 'Speichert …' : 'Zahlung erfassen'}
                  </button>
                  <button className="o-btn o-btn-ghost" onClick={() => setErfassen(null)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---------- Tabelle ---------- */}

          <div className="o-card" id="zahlungsliste">
            <div className="o-card-head">
              <h3>
                {FILTER.find((f) => f.id === filter)?.label}{' '}
                <span className="o-badge o-badge-accent">{liste?.items.length ?? 0}</span>
              </h3>
              {liste?.gekuerzt ? (
                /*
                  Nur noch die LISTE ist gekürzt.

                  Hier stand «auch die Zahlen an den Filtern oben» – und das
                  war wahr: Zähler und Summen entstanden hinter dem `LIMIT
                  500`. Jetzt kommen sie aus einer Aggregatabfrage über alle
                  Rechnungen, und dieser Satz darf das sagen, ohne zu lügen.
                */
                <span className="o-soft" id="zahlungen-kuerzung">
                  Gezeigt werden die {liste.gelesen ?? liste.items.length} neuesten von{' '}
                  {liste.gesamt} Rechnungen. Die Zahlen oben und an den Filtern zählen alle. Für
                  ältere Zeilen bitte suchen.
                </span>
              ) : (
                <span className="o-soft">
                  Zahlungsstatus aus den gültigen Zahlungen gerechnet, nicht gespeichert.
                </span>
              )}
            </div>
            {laedt ? (
              <div className="o-pad">
                <p className="o-muted" style={{ margin: 0 }}>
                  Wird geladen …
                </p>
              </div>
            ) : (liste?.items.length ?? 0) === 0 ? (
              <div className="o-pad">
                <p className="o-muted" style={{ margin: 0 }}>
                  Nichts in dieser Auswahl.
                </p>
              </div>
            ) : (
              <div className="o-tabelle">
                <table className="o-data">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Nummer</th>
                      <th>Kunde</th>
                      <th>Projekt</th>
                      <th style={{ width: 105 }}>Datum</th>
                      <th style={{ width: 105 }}>Fällig</th>
                      <th className="o-num" style={{ width: 110 }}>
                        Total
                      </th>
                      <th className="o-num" style={{ width: 110 }}>
                        Bezahlt
                      </th>
                      <th className="o-num" style={{ width: 110 }}>
                        Offen
                      </th>
                      <th style={{ width: 120 }}>Status</th>
                      <th style={{ width: 210 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {liste?.items.map((z) => (
                      <tr key={z.id} id={`zahl-${z.id}`}>
                        <td className="o-mono">{z.number}</td>
                        <td>{z.customerName}</td>
                        <td className="o-soft">{z.projectName ?? '–'}</td>
                        <td>{datum(z.issueDate)}</td>
                        <td>
                          {datum(z.dueDate)}
                          {z.ueberfaellig && (
                            <div>
                              <span className="o-badge o-badge-alert">überfällig</span>
                            </div>
                          )}
                        </td>
                        <td className="o-num">{rp(z.totalRp)}</td>
                        <td className="o-num">{rp(z.paidRp)}</td>
                        <td className="o-num">
                          <strong>{rp(z.offenRp)}</strong>
                          {z.ueberbezahltRp > 0 && (
                            <div className="o-soft">+{rp(z.ueberbezahltRp)} zu viel</div>
                          )}
                        </td>
                        <td>
                          <span
                            className={`o-badge ${STATUS[z.zahlstatus]?.klasse ?? ''}`}
                            id={`status-${z.id}`}
                          >
                            {STATUS[z.zahlstatus]?.text ?? z.zahlstatus}
                          </span>
                          <div className="o-soft">{z.belegstatus}</div>
                        </td>
                        <td>
                          <div className="office-actions">
                            {/*
                              Auf eine stornierte Rechnung wird nicht gezahlt –
                              und der Knopf dazu steht hier gar nicht erst.

                              Der Server lehnt es ab (`zahlungErfassen`), das
                              war nie die Frage. Die Frage war, warum ein Büro
                              diesen Knopf überhaupt sieht: Man tippt Betrag
                              und Datum ab, drückt, und bekommt eine
                              Fehlermeldung für etwas, das von Anfang an nicht
                              ging. Jetzt steht an derselben Stelle der Grund.
                            */}
                            {z.belegstatus === 'storniert' ? (
                              <span className="o-soft" id={`storniert-${z.id}`}>
                                Storniert – keine Zahlung
                              </span>
                            ) : (
                              <button
                                className="o-btn"
                                id={`erfassen-${z.id}`}
                                disabled={gesperrt}
                                title={gesperrt ? 'Zuerst Zwei-Faktor einrichten' : undefined}
                                onClick={() => erfassenOeffnen(z)}
                              >
                                Zahlung erfassen
                              </button>
                            )}
                            <button
                              className="o-btn o-btn-ghost"
                              id={`oeffnen-rechnung-${z.id}`}
                              onClick={() => aufBeleg?.(z.id)}
                            >
                              Öffnen
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        /* ---------- Zahlungshistorie ---------- */
        <>
          <div className="o-card">
            <div className="o-pad">
              <div className="o-filter" id="historiefilter">
                {(
                  [
                    ['gueltig', 'Gültige'],
                    ['storniert', 'Stornierte'],
                    ['alle', 'Alle']
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    id={`hist-${id}`}
                    className={id === historieZeige ? 'o-chip aktiv' : 'o-chip'}
                    onClick={() => setHistorieZeige(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {korrektur && (
            <div className="o-card" id="korrektur-formular">
              <div className="o-card-head">
                <h3>
                  Zahlung korrigieren · <span className="o-mono">{korrektur.number}</span>
                </h3>
                <span className="o-soft">
                  Bisher: CHF {rp(korrektur.amountRp)} vom {datum(korrektur.paidAt)}
                </span>
              </div>
              <div className="o-pad">
                {/*
                  Was hier passiert, steht hier – und nicht erst in der
                  Rückmeldung. Wer eine Buchung ändert, soll vorher wissen,
                  dass die alte stehen bleibt.
                */}
                <p style={{ marginTop: 0 }}>
                  Die bisherige Zahlung wird storniert und bleibt mit Grund sichtbar; die
                  berichtigte wird neu verbucht. Offener Betrag und Status ergeben sich danach von
                  selbst.
                </p>
                <div className="o-grid o-grid-3">
                  <div className="o-field">
                    <label htmlFor="k-betrag">Betrag CHF</label>
                    <input
                      autoComplete="off"
                      id="k-betrag"
                      inputMode="decimal"
                      value={kBetrag}
                      onChange={(e) => setKBetrag(e.target.value)}
                    />
                  </div>
                  <div className="o-field">
                    <label htmlFor="k-datum">Zahlungsdatum</label>
                    <Datumsfeld
                      id="k-datum"
                      value={kDatum}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={setKDatum}
                    />
                  </div>
                  <div className="o-field">
                    <label htmlFor="k-notiz">Referenz oder Notiz</label>
                    <input
                      autoComplete="off"
                      id="k-notiz"
                      value={kNotiz}
                      onChange={(e) => setKNotiz(e.target.value)}
                      placeholder="z.B. Bankauszug 12.09."
                    />
                  </div>
                </div>
                <div className="o-field" style={{ marginTop: 12, maxWidth: 520 }}>
                  <label htmlFor="k-grund">Grund der Korrektur (Pflicht)</label>
                  <input
                    autoComplete="off"
                    id="k-grund"
                    value={kGrund}
                    onChange={(e) => setKGrund(e.target.value)}
                    placeholder="z.B. Betrag vertippt"
                  />
                </div>
                {/*
                  Die Überzahlung ist eine Entscheidung und kein Versehen.

                  Der Server lehnt einen Betrag ab, der mehr ergibt als die
                  Rechnung fordert – ausser dieses Häkchen sagt ausdrücklich
                  Ja. Der häufigste Korrekturgrund ist eine Stelle zu viel;
                  genau die soll nicht durchrutschen.
                */}
                <label className="o-wahlkasten" style={{ marginTop: 10 }}>
                  <input
                    type="checkbox"
                    id="k-ueberzahlung"
                    checked={kUeberzahlung}
                    onChange={(e) => setKUeberzahlung(e.target.checked)}
                  />
                  <span>Überzahlung bewusst in Kauf nehmen</span>
                </label>
                <div className="office-actions" style={{ marginTop: 14 }}>
                  <button
                    className="o-btn o-btn-primary"
                    id="btn-korrektur-speichern"
                    disabled={busy !== null || !kGrund.trim() || zuRappen(kBetrag) <= 0}
                    onClick={() => void korrigieren()}
                  >
                    {busy === 'korrektur' ? 'Korrigiert …' : 'Korrektur buchen'}
                  </button>
                  <button
                    className="o-btn o-btn-ghost"
                    id="btn-korrektur-abbrechen"
                    onClick={() => setKorrektur(null)}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}

          {storno && (
            <div className="o-card" id="storno-formular">
              <div className="o-card-head">
                <h3>Zahlung stornieren</h3>
              </div>
              <div className="o-pad">
                <p style={{ marginTop: 0 }}>
                  Die Zahlung wird nicht gelöscht. Sie bleibt durchgestrichen sichtbar, zählt nicht
                  mehr zum offenen Betrag, und Person und Zeitpunkt stehen daneben.
                </p>
                <div className="o-field" style={{ maxWidth: 520 }}>
                  <label htmlFor="storno-grund">Grund (Pflicht)</label>
                  <input
                    autoComplete="off"
                    id="storno-grund"
                    value={grund}
                    onChange={(e) => setGrund(e.target.value)}
                    placeholder="z.B. doppelt erfasst"
                  />
                </div>
                <div className="office-actions" style={{ marginTop: 14 }}>
                  <button
                    className="o-btn"
                    id="btn-storno-bestaetigen"
                    disabled={busy !== null || !grund.trim()}
                    onClick={() => void stornieren()}
                  >
                    {busy === 'storno' ? 'Storniert …' : 'Stornieren'}
                  </button>
                  <button
                    className="o-btn o-btn-ghost"
                    onClick={() => {
                      setStorno(null)
                      setGrund('')
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="o-card" id="zahlungshistorie">
            <div className="o-card-head">
              <h3>
                Zahlungen · {zr.label}{' '}
                <span className="o-badge o-badge-accent">{historie?.summe.anzahl ?? 0}</span>
              </h3>
              {/*
                Der Betrag kommt aus derselben Abfrage wie die Kachel oben und
                nicht aus den gezeigten Zeilen. Die Liste trägt eine Grenze;
                die Summe darf keine tragen – sonst stünde hier eine kleinere
                Zahl als in der Kachel, die hierher geführt hat.
              */}
              <span className="o-soft" id="historie-summe">
                {historie
                  ? `${historie.summe.gueltig} gültig · ${historie.summe.storniert} storniert · ` +
                    `CHF ${rp(eingang?.betragRp ?? historie.summe.betragRp)} eingegangen ` +
                    `(${zeitraumText})`
                  : ''}
              </span>
            </div>
            {(historie?.payments.length ?? 0) === 0 ? (
              <div className="o-pad">
                <p className="o-muted" style={{ margin: 0 }}>
                  Noch keine Zahlung erfasst.
                </p>
              </div>
            ) : (
              <div className="o-tabelle">
                <table className="o-data">
                  <thead>
                    <tr>
                      <th style={{ width: 105 }}>Datum</th>
                      <th className="o-num" style={{ width: 120 }}>
                        Betrag
                      </th>
                      <th style={{ width: 130 }}>Rechnung</th>
                      <th>Kunde</th>
                      <th style={{ width: 90 }}>Quelle</th>
                      <th>Notiz</th>
                      <th style={{ width: 150 }}>Erfasst durch</th>
                      <th style={{ width: 150 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {historie?.payments.map((z) => (
                      <tr
                        key={z.id}
                        id={`hzeile-${z.id}`}
                        className={z.voidedAt ? 'storniert' : ''}
                      >
                        <td>{datum(z.paidAt)}</td>
                        <td className="o-num">
                          <strong>{rp(z.amountRp)}</strong>
                        </td>
                        <td className="o-mono">{z.number}</td>
                        <td>{z.customerName}</td>
                        <td className="o-soft">{z.source}</td>
                        <td className="o-soft">
                          {z.note ?? ''}
                          {z.voidedAt && (
                            <div>
                              <span className="o-badge o-badge-alert">storniert</span>{' '}
                              {z.voidReason} · {z.voidedByName ?? ''} ·{' '}
                              {datum(String(z.voidedAt).slice(0, 10))}
                            </div>
                          )}
                        </td>
                        <td className="o-soft">{z.byName ?? '–'}</td>
                        <td>
                          {!z.voidedAt && (
                            <div className="office-actions">
                              {/*
                                Korrigieren steht vor Stornieren, weil es der
                                häufigere Fall ist: Die Zahlung ist eingegangen,
                                nur eine Angabe stimmt nicht. Für eine Zahlung
                                aus einer Bankdatei gibt es den Knopf nicht –
                                dort ist die Datei die Wahrheit, und bleibt nur
                                das Stornieren.
                              */}
                              {z.source === 'manuell' && (
                                <button
                                  className="o-btn o-btn-ghost"
                                  id={`korrigieren-${z.id}`}
                                  disabled={gesperrt}
                                  title={gesperrt ? 'Zuerst Zwei-Faktor einrichten' : undefined}
                                  onClick={() => korrekturOeffnen(z)}
                                >
                                  Korrigieren
                                </button>
                              )}
                              <button
                                className="o-btn o-btn-ghost"
                                id={`storno-${z.id}`}
                                disabled={gesperrt}
                                title={gesperrt ? 'Zuerst Zwei-Faktor einrichten' : undefined}
                                onClick={() => {
                                  setStorno(z.id)
                                  setGrund('')
                                }}
                              >
                                Stornieren
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
